import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import '../Admin/Users.css';
import { formatAppDate, localToday } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../utils/roleUtils';

const ViewCycleCount = forwardRef(({ countId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit, onCountCreated, onStatusChange }, ref) => {
  const { t } = useTranslation('warehouse');
  const { t: tCommon } = useTranslation('common');
  const [count, setCount] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialOptions, setMaterialOptions] = useState([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  // count_item_id -> counted quantity typed but not saved yet
  const [countedInputs, setCountedInputs] = useState({});

  const { showSuccess, showError } = useMessages();
  const { user } = useAuth();
  // Approving adjusts inventory; the API only allows ADMIN and MANAGER
  const canApprove = hasRole(user, 'ADMIN', 'MANAGER');

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave,
    handleStart,
    handleComplete,
    handleApprove
  }));

  useEffect(() => {
    if (countId && !createMode) {
      fetchCount();
    } else if (createMode) {
      const newCount = {
        count_type: 'CYCLE',
        location: '',
        zone: '',
        scheduled_date: localToday(),
        notes: ''
      };
      setCount(newCount);
      setEditData(newCount);
      setItems([]);
    }
  }, [countId, createMode]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await apiFetch(getApiUrl('/api/warehouse-locations?is_active=true&limit=200'));
        if (response.ok) {
          setLocations(await response.json());
        }
      } catch (err) {
        console.error('Error fetching warehouse locations:', err);
      }
    };
    fetchLocations();
  }, []);

  const canAddItems = !createMode && !editMode && (count?.status === 'SCHEDULED' || count?.status === 'IN_PROGRESS');

  useEffect(() => {
    if (!canAddItems) return undefined;
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ page_size: '50' });
        if (materialSearch.trim()) params.set('search', materialSearch.trim());
        const response = await apiFetch(getApiUrl(`/api/inventory?${params.toString()}`));
        if (response.ok) {
          const data = await response.json();
          setMaterialOptions(data.items || []);
        }
      } catch (err) {
        console.error('Error searching materials:', err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [materialSearch, canAddItems]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchCount = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/cycle-counts/${countId}`));
      if (response.ok) {
        const data = await response.json();
        setCount(data);
        setEditData(data);
        setItems(data.items || []);
        setCountedInputs({});
        if (onStatusChange) onStatusChange(data.status);
      } else {
        setError(t('cycleCounts.view.error.fetchFailed', 'Failed to load cycle count'));
      }
    } catch (error) {
      console.error('Error fetching cycle count:', error);
      setError(t('cycleCounts.view.error.loadingError', 'Error loading cycle count'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      if (!editData.count_type) {
        showError(t('cycleCounts.view.validation.required', 'Count type is required'));
        return;
      }
      if (!editData.location) {
        showError(t('cycleCounts.view.validation.locationRequired', 'Choose the location to count'));
        return;
      }

      const saveData = {
        count_type: editData.count_type,
        location: editData.location || null,
        zone: editData.zone || null,
        scheduled_date: editData.scheduled_date || null,
        notes: editData.notes || null
      };

      const url = createMode
        ? getApiUrl('/api/cycle-counts')
        : getApiUrl(`/api/cycle-counts/${countId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const result = await response.json();
        setHasChanges(false);

        if (createMode) {
          showSuccess(t('cycleCounts.view.messages.createdSuccess', 'Cycle count created successfully'));
          if (onCountCreated) {
            onCountCreated(result.cycle_count_id);
          }
        } else {
          showSuccess(t('cycleCounts.view.messages.updatedSuccess', 'Cycle count updated successfully'));
          await fetchCount();
        }

        if (onSave) {
          onSave();
        }
      } else {
        await showApiError(response, showError, tCommon);
        return;
      }
    } catch (error) {
      console.error('Error saving cycle count:', error);
      showError(t('cycleCounts.view.error.loadingError', 'Error saving cycle count'));
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm(t('cycleCounts.view.confirmations.unsavedChanges', 'You have unsaved changes. Are you sure you want to cancel?'))) {
        setEditData(count || {});
        setHasChanges(false);
        if (onCancel) onCancel();
      }
    } else {
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('cycleCounts.view.confirmations.deleteConfirm', 'Are you sure you want to delete this cycle count?'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/cycle-counts/${countId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('cycleCounts.view.messages.deletedSuccess', 'Cycle count deleted successfully'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
        return;
      }
    } catch (error) {
      console.error('Error deleting cycle count:', error);
      showError(t('cycleCounts.view.error.fetchFailed', 'Error deleting cycle count'));
    }
  };

  const handleAddItem = async () => {
    if (!selectedMaterialId) {
      showError(t('cycleCounts.view.validation.materialRequired', 'Choose a material to add'));
      return;
    }
    try {
      const response = await apiFetch(getApiUrl(`/api/cycle-counts/${countId}/items`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([{ inventory_item_id: Number(selectedMaterialId) }])
      });
      if (response.ok) {
        showSuccess(t('cycleCounts.view.messages.itemAdded', 'Material added to the count'));
        setSelectedMaterialId('');
        await fetchCount();
        if (onSave) onSave();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error adding item to cycle count:', error);
      showError(t('cycleCounts.view.messages.itemAddFailed', 'Error adding material'));
    }
  };

  const handleCountedChange = (countItemId, value) => {
    setCountedInputs(prev => ({ ...prev, [countItemId]: value }));
  };

  // Saves the typed quantities. Returns false if nothing could be saved.
  const saveCounts = async () => {
    const entries = Object.entries(countedInputs).filter(([, value]) => value !== '');
    if (entries.length === 0) return true;
    if (entries.some(([, value]) => Number.isNaN(Number(value)) || Number(value) < 0)) {
      showError(t('cycleCounts.view.validation.quantityNonNegative', 'Counted quantity must be zero or more'));
      return false;
    }
    const response = await apiFetch(getApiUrl(`/api/cycle-counts/${countId}/record`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entries.map(([id, value]) => ({ count_item_id: Number(id), counted_quantity: Number(value) })))
    });
    if (!response.ok) {
      await showApiError(response, showError, tCommon);
      return false;
    }
    return true;
  };

  const handleSaveCounts = async () => {
    try {
      if (Object.values(countedInputs).every(value => value === '')) {
        showError(t('cycleCounts.view.validation.nothingToSave', 'Type at least one counted quantity'));
        return;
      }
      if (await saveCounts()) {
        showSuccess(t('cycleCounts.view.messages.countsSaved', 'Counted quantities saved'));
        await fetchCount();
        if (onSave) onSave();
      }
    } catch (error) {
      console.error('Error recording counts:', error);
      showError(t('cycleCounts.view.messages.countsSaveFailed', 'Error saving counted quantities'));
    }
  };

  const handleStart = async () => {
    if (!window.confirm(t('cycleCounts.view.confirmations.startConfirm', 'Start this cycle count? Status will change to IN_PROGRESS.'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/cycle-counts/${countId}/start`), {
        method: 'POST'
      });

      if (response.ok) {
        showSuccess(t('cycleCounts.view.messages.startedSuccess', 'Cycle count started'));
        await fetchCount();
        if (onSave) onSave();
      } else {
        await showApiError(response, showError, tCommon);
        return;
      }
    } catch (error) {
      console.error('Error starting cycle count:', error);
      showError(t('cycleCounts.view.error.loadingError', 'Error starting cycle count'));
    }
  };

  const handleComplete = async () => {
    if (!window.confirm(t('cycleCounts.view.confirmations.completeConfirm', 'Complete counting? All items must be counted.'))) {
      return;
    }

    try {
      if (!(await saveCounts())) return;
      const response = await apiFetch(getApiUrl(`/api/cycle-counts/${countId}/complete`), {
        method: 'POST'
      });

      if (response.ok) {
        showSuccess(t('cycleCounts.view.messages.completedSuccess', 'Cycle count completed'));
        await fetchCount();
        if (onSave) onSave();
      } else {
        await showApiError(response, showError, tCommon);
        await fetchCount();
        return;
      }
    } catch (error) {
      console.error('Error completing cycle count:', error);
      showError(t('cycleCounts.view.error.loadingError', 'Error completing cycle count'));
    }
  };

  const handleApprove = async () => {
    if (!window.confirm(t('cycleCounts.view.confirmations.approveConfirm', 'Approve this cycle count and adjust inventory?'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/cycle-counts/${countId}/approve?adjust_inventory=true`), {
        method: 'POST'
      });

      if (response.ok) {
        showSuccess(t('cycleCounts.view.messages.approvedSuccess', 'Cycle count approved and inventory adjusted'));
        await fetchCount();
        if (onSave) onSave();
      } else {
        await showApiError(response, showError, tCommon);
        return;
      }
    } catch (error) {
      console.error('Error approving cycle count:', error);
      showError(t('cycleCounts.view.error.loadingError', 'Error approving cycle count'));
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'SCHEDULED': 'status-warning',
      'IN_PROGRESS': 'status-active',
      'COMPLETED': 'status-success',
      'APPROVED': 'status-inactive'
    };
    return (
      <span className={`status-badge ${statusMap[status] || 'status-inactive'}`}>
        {t(`cycleCounts.status.${status?.toLowerCase()}`, status)}
      </span>
    );
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('cycleCounts.loading', 'Loading...')}</div></div>;
  }

  if (error && !count) {
    const errorMessage = typeof error === 'string' ? error : 'An error occurred';
    return <div className="view-container"><div className="error-message">{errorMessage}</div></div>;
  }

  if (!count && !createMode) {
    return <div className="view-container"><div className="no-data">{t('cycleCounts.view.titles.noCountFound', 'Cycle count not found')}</div></div>;
  }

  const isScheduled = count?.status === 'SCHEDULED';
  const isInProgress = count?.status === 'IN_PROGRESS';
  const isCompleted = count?.status === 'COMPLETED';
  const isApproved = count?.status === 'APPROVED';
  const canEdit = createMode || (editMode && isScheduled);

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <h2>
            {createMode ? t('cycleCounts.view.titles.newCount', 'New Cycle Count') : count?.count_number || t('cycleCounts.view.titles.viewCount', 'Cycle Count')}
          </h2>
          <span className="header-id">{!createMode && count?.count_type}</span>
        </div>
        <div className="header-status">
          {!createMode && count?.status && getStatusBadge(count.status)}
        </div>
      </div>

      {/* Workflow action buttons */}
      {!createMode && !editMode && (
        <div style={{ padding: '0 16px 8px', display: 'flex', gap: '8px' }}>
          {isScheduled && (
            <button className="btn-primary" onClick={handleStart}>
              {t('cycleCounts.view.actions.start', 'Start Count')}
            </button>
          )}
          {isInProgress && (
            <button className="btn-primary" onClick={handleComplete}>
              {t('cycleCounts.view.actions.complete', 'Complete Count')}
            </button>
          )}
          {isCompleted && canApprove && (
            <button className="btn-primary" onClick={handleApprove}>
              {t('cycleCounts.view.actions.approve', 'Approve & Adjust Inventory')}
            </button>
          )}
          {isCompleted && !canApprove && (
            <span style={{ color: 'var(--text-secondary)' }}>
              {t('cycleCounts.view.pendingApproval', 'Waiting for approval by an administrator or manager.')}
            </span>
          )}
        </div>
      )}

      <div className="view-content">
        <div className="form-section">
          <h3>{t('cycleCounts.view.sections.countInfo', 'Count Information')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('cycleCounts.view.fields.countType', 'Count Type')} *</label>
              <select
                value={editData.count_type || 'CYCLE'}
                onChange={(e) => handleFieldChange('count_type', e.target.value)}
                disabled={!canEdit}
              >
                <option value="CYCLE">{t('cycleCounts.types.cycle', 'Cycle')}</option>
                <option value="SPOT">{t('cycleCounts.types.spot', 'Spot')}</option>
                <option value="FULL">{t('cycleCounts.types.full', 'Full')}</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t('cycleCounts.view.fields.scheduledDate', 'Scheduled Date')}</label>
              <input
                type="date"
                value={editData.scheduled_date || ''}
                onChange={(e) => handleFieldChange('scheduled_date', e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="form-group">
              <label>{t('cycleCounts.view.fields.location', 'Location')} *</label>
              <select
                value={editData.location || ''}
                onChange={(e) => handleFieldChange('location', e.target.value)}
                disabled={!canEdit}
              >
                <option value="">{t('cycleCounts.view.placeholders.location', 'Select a location')}</option>
                {editData.location && !locations.some(l => l.location_code === editData.location) && (
                  <option value={editData.location}>{editData.location}</option>
                )}
                {locations.map(l => (
                  <option key={l.location_code} value={l.location_code}>
                    {l.location_code}{l.location_name && l.location_name !== l.location_code ? ` - ${l.location_name}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('cycleCounts.view.fields.zone', 'Zone')}</label>
              <input
                type="text"
                value={editData.zone || ''}
                onChange={(e) => handleFieldChange('zone', e.target.value)}
                disabled={!canEdit}
                placeholder={t('cycleCounts.view.placeholders.zone', 'e.g. A1')}
              />
            </div>
            <div className="form-group full-width">
              <label>{t('cycleCounts.view.fields.notes', 'Notes')}</label>
              <textarea
                value={editData.notes || ''}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                disabled={!canEdit}
                rows="3"
              />
            </div>
          </div>
        </div>

        {/* Items section - only shown when viewing existing count */}
        {!createMode && (
          <div className="form-section">
            <h3>{t('cycleCounts.view.sections.items', 'Count Items')} ({items.length})</h3>

            {canAddItems && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={materialSearch}
                  onChange={(e) => setMaterialSearch(e.target.value)}
                  placeholder={t('cycleCounts.view.placeholders.materialSearch', 'Search material by code or name')}
                  style={{ flex: '1 1 200px' }}
                />
                <select
                  value={selectedMaterialId}
                  onChange={(e) => setSelectedMaterialId(e.target.value)}
                  style={{ flex: '2 1 260px' }}
                  aria-label={t('cycleCounts.view.fields.material', 'Material')}
                >
                  <option value="">{t('cycleCounts.view.placeholders.material', 'Select a material')}</option>
                  {materialOptions.map(m => (
                    <option key={m.item_id} value={m.item_id}>{m.item_code} - {m.item_name}</option>
                  ))}
                </select>
                <button className="btn-secondary" onClick={handleAddItem}>
                  + {t('cycleCounts.view.actions.addItem', 'Add material')}
                </button>
              </div>
            )}

            {items.length === 0 ? (
              <div className="no-data">
                {t('cycleCounts.view.noItems', 'No materials in this count yet. Add the materials to count at this location.')}
              </div>
            ) : (
            <div className="list-table" style={{ maxHeight: '300px', overflow: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>{t('cycleCounts.view.itemHeaders.itemCode', 'Item Code')}</th>
                    <th>{t('cycleCounts.view.itemHeaders.itemName', 'Item Name')}</th>
                    <th>{t('cycleCounts.view.itemHeaders.location', 'Location')}</th>
                    <th>{t('cycleCounts.view.itemHeaders.systemQty', 'System Qty')}</th>
                    <th>{t('cycleCounts.view.itemHeaders.countedQty', 'Counted Qty')}</th>
                    <th>{t('cycleCounts.view.itemHeaders.variance', 'Variance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const variance = item.variance_quantity != null ? Number(item.variance_quantity) : null;
                    return (
                    <tr key={item.count_item_id}>
                      <td><span className="dept-code">{item.item_code || '-'}</span></td>
                      <td>{item.item_name || '-'}</td>
                      <td>{item.location || '-'}</td>
                      <td>{item.system_quantity != null ? Number(item.system_quantity) : '-'}</td>
                      <td>
                        {isInProgress ? (
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={countedInputs[item.count_item_id] ?? (item.counted_quantity != null ? Number(item.counted_quantity) : '')}
                            onChange={(e) => handleCountedChange(item.count_item_id, e.target.value)}
                            placeholder={t('cycleCounts.view.notCounted', 'Not counted')}
                            aria-label={`${t('cycleCounts.view.itemHeaders.countedQty', 'Counted Qty')} ${item.item_code || ''}`}
                            style={{ width: '110px' }}
                          />
                        ) : (
                          <strong style={{ color: item.counted_quantity != null ? 'inherit' : 'var(--text-secondary)' }}>
                            {item.counted_quantity != null ? Number(item.counted_quantity) : t('cycleCounts.view.notCounted', 'Not counted')}
                          </strong>
                        )}
                      </td>
                      <td>
                        {variance != null ? (
                          <span style={{ color: variance !== 0 ? 'var(--status-error, #f44336)' : 'var(--status-success, #28a745)' }}>
                            {variance > 0 ? '+' : ''}{variance}
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}

            {isInProgress && items.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button className="btn-primary" onClick={handleSaveCounts}>
                  {t('cycleCounts.view.actions.saveCounts', 'Save counted quantities')}
                </button>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                  {t('cycleCounts.view.saveCountsHint', 'System quantity is taken when you save, so record each quantity right after counting.')}
                </span>
              </div>
            )}

            {/* Variance summary */}
            {count?.total_variance_value != null && count.total_variance_value !== 0 && (
              <div style={{ marginTop: '8px', padding: '8px 12px', background: 'var(--bg-tertiary, #f5f5f5)', borderRadius: '4px' }}>
                <strong>{t('cycleCounts.view.sections.varianceSummary', 'Total Variance Value')}:</strong>{' '}
                <span style={{ color: 'var(--status-error, #f44336)' }}>
                  ${Math.abs(count.total_variance_value).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* System info */}
        {count && count.created_date && !createMode && (
          <div className="form-section">
            <h3>{t('cycleCounts.view.sections.systemInfo', 'System Information')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('cycleCounts.view.fields.createdDate', 'Created')}</label>
                <span>{formatAppDate(count.created_date, 'datetime')}</span>
              </div>
              {count.counter_name && (
                <div className="info-item">
                  <label>{t('cycleCounts.view.fields.countedBy', 'Counted By')}</label>
                  <span>{count.counter_name}</span>
                </div>
              )}
              {count.count_date && (
                <div className="info-item">
                  <label>{t('cycleCounts.view.fields.countDate', 'Count Date')}</label>
                  <span>{formatAppDate(count.count_date, 'datetime')}</span>
                </div>
              )}
              {count.approver_name && (
                <div className="info-item">
                  <label>{t('cycleCounts.view.fields.approvedBy', 'Approved By')}</label>
                  <span>{count.approver_name}</span>
                </div>
              )}
              {count.approved_date && (
                <div className="info-item">
                  <label>{t('cycleCounts.view.fields.approvedDate', 'Approved Date')}</label>
                  <span>{formatAppDate(count.approved_date, 'datetime')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ViewCycleCount;
